import React from 'react';
import { Checkbox, Dropdown } from "semantic-ui-react";
import ExportCdpReport from "../ExportCdpReport";
import DropdownWithReset from "./DropdownWithReset";
import { 
    daysType, 
    payType, 
    udaRoleOptions, 
    notesReminderFilterOptions,
    customContractFilterOptions
} from "../../../constants";
import { getColumnOptions } from "../services";

/**
 * Filter controls for CDP Reports Table
 * Provides all filter dropdowns and export functionality
 */
const ReportFilterControls = ({
    // Filter data
    contractNameOptions = [],
    dentistOptions = [],
    regionListOptions = [],
    practiceOptions = [],
    isActiveOptions = [],
    brandOptions = [],
    supportUserOptions = [],
    rampDateOptions = [],
    sageAccounts = [],
    
    // Current filter state
    filters = {},
    hideColumn = {},
    showAsterisk = false,
    
    // Callbacks
    onChangeFilter,
    onChangeHideColumn,
    onToggleAsterisk,
    excludedProvidersControl = null,
    
    // For export
    reportData = [],
    getPracticeName,
    scheduleHours,
    collectionDentistData,
    getDoctorFullName
}) => {
    /**
     * Normalizes dropdown options to consistent format
     */
    const normalizeOptions = (opts = []) => (opts || []).map(opt => {
        if (opt === null || opt === undefined) return null;
        if (typeof opt === 'string' || typeof opt === 'number') {
            return {
                key: String(opt),
                text: String(opt),
                value: opt
            };
        }
        const key = opt.key ?? opt.value ?? opt.text ?? JSON.stringify(opt);
        const text = opt.text ?? (opt.value ?? key);
        const value = opt.value ?? opt.text ?? key;
        return { key, text, value };
    }).filter(Boolean);

    const contractOptions = normalizeOptions(contractNameOptions).filter(
        o => o.value !== null && o.value !== undefined
    );
    const brandOptionsNormalized = normalizeOptions(brandOptions);
    const columnOptions = getColumnOptions();

    return (
        <div className="cdp-filter-area">

            {/* Row 1: Provider & location filters */}
            <div className="cdp-filter-group">
                <DropdownWithReset
                    name="dentist"
                    placeholder='Provider'
                    options={dentistOptions}
                    value={filters.dentist}
                    onChange={onChangeFilter}
                />
                <Dropdown
                    placeholder='Status'
                    selection
                    search
                    name="isActive"
                    options={isActiveOptions}
                    onChange={onChangeFilter}
                    defaultValue={filters.isActive}
                />
                <Dropdown
                    placeholder='Region'
                    selection
                    search
                    name="region"
                    options={regionListOptions}
                    onChange={onChangeFilter}
                    className={filters.region ? 'isStrictFilter' : ''}
                />
                <DropdownWithReset
                    name="practiceName"
                    placeholder='Practice'
                    options={practiceOptions}
                    value={filters.practiceName}
                    onChange={onChangeFilter}
                    className={filters.practiceName ? 'float-right isStrictFilter' : 'float-right'}
                />
                <Dropdown
                    placeholder='Brand'
                    selection
                    multiple
                    search
                    name="brandName"
                    options={brandOptionsNormalized}
                    onChange={onChangeFilter}
                    value={filters.brandName}
                    className={filters.brandName?.length > 0 && !filters.brandName.includes('All') ? 'isStrictFilter' : ''}
                />
                <Dropdown
                    placeholder='UDA Role'
                    selection
                    search
                    multiple={true}
                    name="role"
                    options={udaRoleOptions && udaRoleOptions.filter(opt => opt.value !== null)}
                    onChange={onChangeFilter}
                    className={(filters.role || []).filter(r => r === 'Dentist' || r === 'Oral Surgeon').length === 1 ? 'isStrictFilter' : ''}
                />
                <Dropdown
                    placeholder='Support User'
                    selection
                    search
                    name="supportUser"
                    options={supportUserOptions}
                    value={filters.supportUser}
                    onChange={onChangeFilter}
                />
            </div>

            {/* Row 2: Contract, pay & display filters */}
            <div className="cdp-filter-group">
                <DropdownWithReset
                    name="contractType"
                    placeholder='Contract Type'
                    options={contractOptions}
                    value={filters.contractType}
                    onChange={onChangeFilter}
                    multiple={true}
                />
                <DropdownWithReset
                    name="customContract"
                    placeholder='Custom Contract'
                    options={customContractFilterOptions}
                    value={filters.customContract}
                    onChange={onChangeFilter}
                />
                <DropdownWithReset
                    name="greaterOfCategory"
                    placeholder='GO Category'
                    options={[
                        { key: 'A', text: 'Category A', value: 'A' },
                        { key: 'B', text: 'Category B', value: 'B' },
                        { key: 'C', text: 'Category C', value: 'C' },
                        { key: 'not_defined', text: 'Not Defined', value: 'not_defined' },
                    ]}
                    value={filters.greaterOfCategory}
                    onChange={onChangeFilter}
                    multiple={true}
                />
                <Dropdown
                    placeholder='Ramp'
                    selection
                    search
                    name="rampDate"
                    options={rampDateOptions}
                    onChange={onChangeFilter}
                />
                <Dropdown
                    placeholder='Base Pay'
                    selection
                    search
                    name="payType"
                    options={payType}
                    onChange={onChangeFilter}
                />
                <Dropdown
                    placeholder='Day Count'
                    selection
                    search
                    name="daysType"
                    options={daysType}
                    onChange={onChangeFilter}
                />
                <Dropdown
                    placeholder='Notes Reminder'
                    selection
                    search
                    name="notesReminder"
                    options={notesReminderFilterOptions}
                    onChange={onChangeFilter}
                />
                <div style={{ background: 'rgba(144,238,100,0.18)', borderRadius: '6px', padding: '2px' }}>
                    <Dropdown
                        placeholder='Account'
                        selection
                        search
                        multiple
                        name="account"
                        options={sageAccounts}
                        value={filters.account}
                        onChange={onChangeFilter}
                    />
                </div>
                <div style={{ background: 'rgba(255,99,99,0.15)', borderRadius: '6px', padding: '2px' }}>
                    <Dropdown
                        placeholder='Exclude Account'
                        selection
                        search
                        multiple
                        name="excludeAccount"
                        options={sageAccounts}
                        value={filters.excludeAccount}
                        onChange={onChangeFilter}
                    />
                </div>
                {excludedProvidersControl}
                <Dropdown
                    placeholder='Hide Columns'
                    selection
                    defaultValue={Object.keys(hideColumn)}
                    multiple
                    search
                    name="hideColumn"
                    options={columnOptions}
                    onChange={onChangeHideColumn}
                />
            </div>

            {/* Row 3: Checkboxes + actions */}
            <div className="cdp-filter-checks">
                <Checkbox
                    label='Show Asterisk'
                    checked={showAsterisk}
                    onClick={onToggleAsterisk}
                />
                <Checkbox
                    label="Low Care (<$200/day/practice)"
                    name="lowCareByPractice"
                    checked={filters.lowCareByPractice}
                    onChange={(e, data) => onChangeFilter(e, { name: 'lowCareByPractice', value: data.checked })}
                />
                <Checkbox
                    label="Worked Half Day or Less"
                    name="workedHalfDay"
                    checked={filters.workedHalfDay}
                    onChange={(e, data) => onChangeFilter(e, { name: 'workedHalfDay', value: data.checked })}
                />
                <Checkbox
                    label="Other Practice Making Base"
                    name="otherPracticeMakingBase"
                    checked={filters.otherPracticeMakingBase}
                    onChange={(e, data) => onChangeFilter(e, { name: 'otherPracticeMakingBase', value: data.checked })}
                />
                <Checkbox
                    label="Show Locked At Least One Practice"
                    name="showLockedAtLeastOnePractice"
                    checked={!!filters.showLockedAtLeastOnePractice}
                    onChange={(e, data) => onChangeFilter(e, { name: 'showLockedAtLeastOnePractice', value: data.checked })}
                />
                {filters.otherPracticeMakingBase && (
                    <Checkbox
                        label="Highlight Base / Over Base"
                        name="highlightBaseStatus"
                        checked={filters.highlightBaseStatus}
                        onChange={(e, data) => onChangeFilter(e, { name: 'highlightBaseStatus', value: data.checked })}
                    />
                )}
                {filters.contractType === 'Sum Of' && (
                    <Checkbox
                        label="TED"
                        name="tedFilter"
                        checked={filters.tedFilter}
                        onChange={(e, data) => onChangeFilter(e, { name: 'tedFilter', value: data.checked })}
                    />
                )}
                <Checkbox
                    label="Enable bCare Pay Overrides"
                    name="enableBcareOverride"
                    checked={!!filters.enableBcareOverride}
                    onChange={(e, data) => onChangeFilter(e, { name: 'enableBcareOverride', value: data.checked })}
                />
                <div className="cdp-filter-checks__export">
                    <ExportCdpReport
                        records={reportData}
                        getPracticeName={getPracticeName}
                        scheduleHours={scheduleHours}
                        collectionsData={collectionDentistData}
                        getDoctorFullName={getDoctorFullName}
                    />
                </div>
            </div>

        </div>
    );
};

export default ReportFilterControls;
